import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Eye, RefreshCw, X, XCircle } from 'lucide-react';
import { useT } from '../i18n/useT.js';

export default function JobsPage() {
  const t = useT();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [onlyScriptErrors, setOnlyScriptErrors] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedRenderRequest, setSelectedRenderRequest] = useState(null);
  const [payloadLoading, setPayloadLoading] = useState(false);
  const [payloadError, setPayloadError] = useState('');

  const load = async () => {
    setLoading(true);
    try { const res = await api.getJobs(); setJobs(res.items || []); } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!selectedJobId) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setSelectedJobId(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedJobId]);

  const statusBadge = (s) => {
    const map = { succeeded: 'badge-success', failed: 'badge-error', running: 'badge-warning', queued: 'badge-info', canceled: 'badge-error' };
    return <span className={`badge ${map[s] || ''}`}>{s}</span>;
  };

  const hasScriptErrors = (job) => {
    const summary = job?.scriptExecution;
    if (!summary) return false;
    const beforeFailed = Number(summary.beforeRender?.failed || 0);
    const afterFailed = Number(summary.afterRender?.failed || 0);
    return (beforeFailed + afterFailed) > 0;
  };

  const scriptSummaryText = (job) => {
    const summary = job?.scriptExecution;
    if (!summary) return '-';

    const b = summary.beforeRender || {};
    const a = summary.afterRender || {};
    const executed = Number(b.executed || 0) + Number(a.executed || 0);
    const failed = Number(b.failed || 0) + Number(a.failed || 0);

    if (executed === 0 && failed === 0) return '-';
    return failed > 0
      ? t('jobs.scriptSummaryWithErrors', '{{executed}} ok / {{failed}} errors', { executed, failed })
      : t('jobs.scriptSummaryOk', '{{executed}} ok', { executed });
  };

  const visibleJobs = onlyScriptErrors ? jobs.filter(hasScriptErrors) : jobs;

  const openPayloadModal = async (jobId) => {
    setSelectedJobId(jobId);
    setPayloadLoading(true);
    setPayloadError('');
    setSelectedRenderRequest(null);

    try {
      const detail = await api.getJob(jobId);
      setSelectedRenderRequest(detail?.renderRequest || { data: {}, options: {}, template: null });
    } catch {
      setPayloadError(t('jobs.payload.loadError', 'Could not load payload data'));
    } finally {
      setPayloadLoading(false);
    }
  };

  const closePayloadModal = () => {
    setSelectedJobId(null);
  };

  return (
    <>
      <div className="toolbar">
        <h2>{t('jobs.title', 'Jobs')}</h2>
        <div className="toolbar-actions">
          <button
            className="btn-secondary"
            onClick={() => setOnlyScriptErrors((value) => !value)}
            aria-pressed={onlyScriptErrors}
          >
            {onlyScriptErrors
              ? t('jobs.showAll', 'Show all')
              : t('jobs.onlyScriptErrors', 'Only script errors')}
          </button>
          <button className="btn-secondary" onClick={load}><RefreshCw size={14} /> {t('jobs.refresh', 'Refresh')}</button>
        </div>
      </div>
      <div className="page-content">
        {loading && visibleJobs.length === 0 ? <p>{t('jobs.loading', 'Loading...')}</p> : visibleJobs.length === 0 ? (
          <div className="empty-state"><p>{t('jobs.empty', 'No jobs')}</p></div>
        ) : (
          <table className="table">
            <thead><tr><th>{t('jobs.table.id', 'ID')}</th><th>{t('jobs.table.type', 'Type')}</th><th>{t('jobs.table.status', 'Status')}</th><th>{t('jobs.table.scripts', 'Scripts')}</th><th>{t('jobs.table.created', 'Created')}</th><th>{t('jobs.table.started', 'Started')}</th><th>{t('jobs.table.finished', 'Finished')}</th><th>{t('jobs.table.actions', 'Actions')}</th></tr></thead>
            <tbody>
              {visibleJobs.map((j) => (
                <tr key={j.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{j.id.slice(0, 12)}...</td>
                  <td>{j.jobType || 'render'}</td>
                  <td>{statusBadge(j.status)}</td>
                  <td>{scriptSummaryText(j)}</td>
                  <td>{new Date(j.created_at).toLocaleString()}</td>
                  <td>{j.started_at ? new Date(j.started_at).toLocaleString() : '-'}</td>
                  <td>{j.finished_at ? new Date(j.finished_at).toLocaleString() : '-'}</td>
                  <td>
                    <div className="jobs-row-actions">
                      <button
                        className="btn-secondary"
                        style={{ padding: '4px 8px' }}
                        onClick={() => openPayloadModal(j.id)}
                      >
                        <Eye size={12} /> {t('jobs.viewPayload', 'View payload')}
                      </button>
                      {(j.status === 'queued' || j.status === 'running') && (
                        <button className="btn-danger" style={{ padding: '4px 8px' }} onClick={() => api.cancelJob(j.id).then(load)}>
                          <XCircle size={12} /> {t('jobs.cancel', 'Cancel')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedJobId && (
        <div
          className="jobs-payload-modal-backdrop"
          role="presentation"
          onClick={closePayloadModal}
        >
          <div
            className="jobs-payload-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t('jobs.payload.title', 'Render request payload')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="jobs-payload-modal-header">
              <h3>{t('jobs.payload.title', 'Render request payload')}</h3>
              <button className="btn-secondary" style={{ padding: '4px 8px' }} onClick={closePayloadModal}>
                <X size={12} /> {t('jobs.payload.close', 'Close')}
              </button>
            </div>
            <p className="jobs-payload-modal-job">{t('jobs.payload.jobId', 'Job')}: {selectedJobId}</p>

            {payloadLoading ? (
              <p>{t('jobs.payload.loading', 'Loading payload...')}</p>
            ) : payloadError ? (
              <p className="error-text">{payloadError}</p>
            ) : (
              <pre className="jobs-payload-json">{JSON.stringify(selectedRenderRequest || {}, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </>
  );
}
